import { useServerFlags } from "@/components/global/serverflags";
import { useSocket } from "@/components/global/socket";
import { ClientRequests } from "@/src/enum/socket";
import { GetWrapupType } from "@/src/server/models/seasonal/wrapup/modules/getwrapup";
import { useSession } from "next-auth/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface WrapupContextProps {
    getWrapup: (userIdParam: number) => Promise<void>;
    hasWrapup: boolean;
    wrapupData: GetWrapupType;
}

const WrapupContext = createContext<WrapupContextProps>({
    getWrapup: async (userIdParam: number) => { },
    hasWrapup: false,
    wrapupData: null,
});

export default function WrapupContextProvider({ children }) {
    const { socket } = useSocket();
    const { data: session } = useSession();
    const serverFlags = useServerFlags();
    const userId = session?.user?.id;
    const isWrapupEnabled = serverFlags?.wrapup === true;
    const [hasWrapup, setWrapup] = useState<boolean>(false);
    const [wrapupData, setWrapupData] = useState<GetWrapupType>(null);

    useEffect(
        () => {
            if (!socket || !userId || !isWrapupEnabled) {
                // These values belong to the active user/socket/feature scope.
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setWrapup(false);
                setWrapupData(null);
                return;
            }

            let isActive = true;

            const checkIfHasWrapup = async (): Promise<void> => {
                try {
                    const result = await socket.emitWithAck(ClientRequests.HasWrapup);
                    if (isActive) {
                        setWrapup(result);
                    }
                } catch (err) {
                    console.error('Error fetching wrapup status', err);

                    if (isActive) {
                        setWrapup(false);
                    }
                }
            };

            void checkIfHasWrapup();

            return () => {
                isActive = false;
            };
        },
        [isWrapupEnabled, socket, userId],
    );

    const getWrapup = useCallback(
        async (userIdParam: number) => {
            if (!socket || !userId) {
                return;
            }

            try {
                const result = await socket.emitWithAck(ClientRequests.GetWrapup, userIdParam);
                setWrapupData(result);
            } catch (err) {
                console.error('Error fetching wrapup', err);
                setWrapupData(null);
            }
        },
        [socket, userId],
    );

    const value = useMemo(
        () => ({
            getWrapup: getWrapup,
            hasWrapup: hasWrapup,
            wrapupData: wrapupData,
        }),
        [
            getWrapup,
            hasWrapup,
            wrapupData,
        ],
    );

    return (
        <WrapupContext.Provider value={value}>
            {children}
        </WrapupContext.Provider>
    );
}

export function useWrapup() {
    return useContext(WrapupContext);
}
